import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { ArrowLeft, Save, X } from 'lucide-react'
import { Button, Input, Select, Card, CardHeader, CardBody, CardFooter } from '../../components/ui'
import type { StoreFormData } from '../../types'

// Mock data
const mockStore = {
  id: '1',
  companyId: 'comp-1',
  code: 'SHP001',
  name: '渋谷道玄坂店',
  latitude: 35.659517,
  longitude: 139.698962,
  prefecture: '東京都',
  city: '渋谷区',
  address: '道玄坂2-1-1 渋谷ビル3F',
  status: 'active' as const,
}

const companies = [
  { value: 'comp-1', label: 'テクノブラッド株式会社' },
]

const prefectures = [
  { value: '東京都', label: '東京都' },
  { value: '神奈川県', label: '神奈川県' },
  { value: '大阪府', label: '大阪府' },
  { value: '愛知県', label: '愛知県' },
  { value: '北海道', label: '北海道' },
  { value: '福岡県', label: '福岡県' },
]

const statuses = [
  { value: 'active', label: '稼働中' },
  { value: 'inactive', label: '停止中' },
  { value: 'maintenance', label: 'メンテナンス' },
]

export default function StoreEditPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const store = mockStore

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<StoreFormData>({
    defaultValues: {
      companyId: store.companyId,
      code: store.code,
      name: store.name,
      latitude: store.latitude,
      longitude: store.longitude,
      prefecture: store.prefecture,
      city: store.city,
      address: store.address,
      status: store.status,
    },
  })

  const onSubmit = async (data: StoreFormData) => {
    setIsSubmitting(true)
    try {
      console.log('Update store:', id, data)
      await new Promise(resolve => setTimeout(resolve, 1000))
      navigate(`/stores/${id}`)
    } catch (error) {
      console.error('Failed to update store:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/stores" className="breadcrumb-item flex items-center">
          <ArrowLeft className="w-4 h-4 mr-1" />
          店舗一覧
        </Link>
        <span className="breadcrumb-separator">/</span>
        <Link to={`/stores/${id}`} className="breadcrumb-item">
          {store.name}
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="text-gray-900">編集</span>
      </div>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">店舗を編集</h1>
        <p className="text-sm text-gray-500 mt-1">{store.name}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Basic Info */}
        <Card className="mb-6">
          <CardHeader>
            <h3 className="font-semibold text-gray-900">基本情報</h3>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="店舗コード"
                placeholder="SHP001"
                required
                hint="一意の識別コード"
                error={errors.code?.message}
                {...register('code', { required: '店舗コードは必須です' })}
              />
              <Input
                label="店舗名"
                placeholder="渋谷道玄坂店"
                required
                error={errors.name?.message}
                {...register('name', { required: '店舗名は必須です' })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="企業"
                options={companies}
                required
                {...register('companyId')}
              />
              <Select
                label="ステータス"
                options={statuses}
                {...register('status')}
              />
            </div>
          </CardBody>
        </Card>

        {/* Location */}
        <Card className="mb-6">
          <CardHeader>
            <h3 className="font-semibold text-gray-900">所在地</h3>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="都道府県"
                options={prefectures}
                placeholder="都道府県を選択"
                {...register('prefecture')}
              />
              <Input
                label="市区町村"
                placeholder="渋谷区"
                {...register('city')}
              />
            </div>

            <Input
              label="住所詳細"
              placeholder="道玄坂2-1-1 渋谷ビル3F"
              {...register('address')}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                type="number"
                step="0.000001"
                label="緯度"
                placeholder="35.659517"
                hint="小数点以下6桁まで"
                {...register('latitude', { valueAsNumber: true })}
              />
              <Input
                type="number"
                step="0.000001"
                label="経度"
                placeholder="139.698962"
                hint="小数点以下6桁まで"
                {...register('longitude', { valueAsNumber: true })}
              />
            </div>

            <Button type="button" variant="secondary" size="sm">
              🗺️ 地図から位置を設定
            </Button>
          </CardBody>
        </Card>

        {/* Actions */}
        <Card>
          <CardFooter className="flex justify-between">
            <p className="text-sm text-gray-500">
              {isDirty ? '変更があります' : '変更なし'}
            </p>
            <div className="flex gap-3">
              <Button 
                type="button" 
                variant="secondary"
                leftIcon={<X className="w-4 h-4" />}
                onClick={() => navigate(`/stores/${id}`)}
              >
                キャンセル
              </Button>
              <Button 
                type="submit" 
                isLoading={isSubmitting}
                disabled={!isDirty}
                leftIcon={<Save className="w-4 h-4" />}
              >
                変更を保存
              </Button>
            </div>
          </CardFooter>
        </Card>
      </form>
    </div>
  )
}

